//frontend\src\features\home\pages\Homepage.js
import React from "react";
import SEO from "../../../components/SEO";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import HomeSchema from "components/HomeSchema";
import {
  faLinkedin,
  faYoutube,
  faInstagram,
} from "@fortawesome/free-brands-svg-icons";
import { faVideo, faLink, faBars } from "@fortawesome/free-solid-svg-icons";
import { MessageSquare, Sparkles, ArrowRight, Shield, Heart, Star,Quote, FileText, Share2, CheckCircle, Zap as ZapIcon } from 'lucide-react';


import Navbar from "../pages/Navbar";
import Footer from "../../dashboard/pages/Footer";

const homepageFaqs = [
  {
    question: "How do I collect testimonials?",
    answer:
      "Create a workspace, copy your public testimonial request link, and share it with customers through email, WhatsApp, LinkedIn, or your website.",
  },
  {
    question: "Can I customize testimonials and widgets?",
    answer:
      "Yes. You can control the Wall of Love layout, choose what testimonials to publish, and later extend branding options such as colors, logos, and themes.",
  },
  {
    question: "Do you support video testimonials?",
    answer:
      "Yes. Customers can submit text testimonials, upload videos, and use browser-based video recording where enabled in the collection flow.",
  },
  {
    question: "How do I embed testimonials on my site?",
    answer:
      "Use the generated iframe or script embed code from your workspace and paste it into your website, landing page, CMS, or no-code builder.",
  },
  {
    question: "Can I approve testimonials before publishing?",
    answer:
      "Yes. New submissions stay in moderation first. Only approved and selected testimonials appear on your public Wall of Love or website embeds.",
  },
];

const Homepage = () => {
  return (
    <>
    <SEO
    title="Collect Text & Video Testimonials"
    description="TestimonialsHub helps businesses collect customer testimonials, manage approvals, and publish beautiful Wall of Love widgets on any website."
    keywords="testimonial software, video testimonial software, wall of love widget, collect customer testimonials, testimonial collection platform"
    path="/"
  />
  <HomeSchema />
    <div className="min-h-screen bg-gradient-to-r from-pink-400 to-orange-400">
      
     
      {/* Navbar */}
      <Navbar />

      {/* MODERN HERO SECTION */}
<section id="product" className="relative overflow-hidden py-24 px-6 scroll-mt-28">

  {/* Background */}
  <div className="absolute inset-0 bg-gradient-to-br from-[#fff7f5] via-[#fff] to-[#f8f5ff]" />

  {/* Glow Effects */}
  <div className="absolute top-20 left-20 w-72 h-72 bg-pink-300/20 blur-[120px] rounded-full"></div>
  <div className="absolute bottom-10 right-10 w-72 h-72 bg-purple-300/20 blur-[120px] rounded-full"></div>

  <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">

    {/* LEFT CONTENT */}
    <div>

      {/* Badge */}
      <div className="inline-flex items-center gap-2 bg-white shadow-md border border-gray-100 rounded-full px-4 py-2 mb-8">
        <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
        <span className="text-sm text-gray-600 font-medium">
          Social proof for modern businesses
        </span>
      </div>

      {/* Heading */}
      <h1 className="text-5xl md:text-6xl font-black leading-[1.1] text-gray-900 mb-6">

        Collect testimonials <br />

        <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-orange-400 bg-clip-text text-transparent">
          that build trust.
        </span>
      </h1>

      {/* Description */}
      <p className="text-lg text-gray-500 leading-relaxed max-w-xl mb-10">
        Collect, manage, and showcase authentic customer testimonials
        with beautiful widgets, video reviews, and social proof tools.
      </p>

      {/* CTA */}
      <div className="flex flex-col sm:flex-row gap-4">

       <Link
          to="/signup"
          className="px-8 py-4 rounded-2xl bg-gradient-to-r from-pink-500 via-purple-500 to-orange-400 text-white font-semibold shadow-xl hover:scale-105 transition-all duration-300 text-center"
        >
          Start Free
        </Link>

        <a
          href="#demo"
          className="px-8 py-4 rounded-2xl border border-gray-200 bg-white text-gray-700 font-semibold hover:bg-gray-50 transition text-center"
        >
          See Demo
        </a>


      </div>

      {/* Stats */}
      <div className="flex items-center gap-10 mt-12">

        <div>
          <h3 className="text-3xl font-bold text-gray-900">10k+</h3>
          <p className="text-gray-500 text-sm">Testimonials Collected</p>
        </div>

        <div>
          <h3 className="text-3xl font-bold text-gray-900">4.9★</h3>
          <p className="text-gray-500 text-sm">Customer Rating</p>
        </div>

        <div>
          <h3 className="text-3xl font-bold text-gray-900">99%</h3>
          <p className="text-gray-500 text-sm">Happy Customers</p>
        </div>
      </div>
    </div>

    {/* RIGHT VISUAL */}
    <div className="relative flex justify-center">

      {/* Floating Icons */}
      <div className="absolute -left-4 top-16 w-14 h-14 rounded-2xl bg-white shadow-xl flex items-center justify-center animate-bounce">
        <FontAwesomeIcon
          icon={faLinkedin}
          className="text-blue-600 text-2xl"
        />
      </div>

      <div className="absolute right-0 top-8 w-14 h-14 rounded-2xl bg-white shadow-xl flex items-center justify-center animate-pulse">
        <FontAwesomeIcon
          icon={faInstagram}
          className="text-pink-500 text-2xl"
        />
      </div>

      <div className="absolute -right-6 bottom-20 w-14 h-14 rounded-2xl bg-white shadow-xl flex items-center justify-center animate-bounce">
        <FontAwesomeIcon
          icon={faYoutube}
          className="text-red-500 text-2xl"
        />
      </div>

      {/* Main Card */}
      <div className="relative w-full max-w-md bg-white rounded-[2rem] shadow-[0_30px_80px_rgba(0,0,0,0.08)] border border-gray-100 p-8">

        {/* Top */}
        <div className="flex items-center justify-between mb-8">

          <div>
            <p className="text-sm text-gray-400 mb-1">
              Testimonials Growth
            </p>

            <h3 className="text-2xl font-bold text-gray-900">
              +3,420 Reviews
            </h3>
          </div>

          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-500 via-purple-500 to-orange-400 flex items-center justify-center shadow-lg">
            <MessageSquare className="w-7 h-7 text-white" />
          </div>
        </div>

        {/* Graph Bars */}
        <div className="space-y-5">

          {[
            { label: "Video Testimonials", value: "90%", width: "w-[90%]" },
            { label: "Text Reviews", value: "75%", width: "w-[75%]" },
            { label: "Wall Embeds", value: "65%", width: "w-[65%]" },
            { label: "Social Proof", value: "55%", width: "w-[55%]" },
          ].map((item, i) => (
            <div key={i}>

              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">
                  {item.label}
                </span>

                <span className="text-sm font-semibold text-gray-800">
                  {item.value}
                </span>
              </div>

              <div className="w-full h-3 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className={`h-full rounded-full bg-gradient-to-r from-pink-500 via-purple-500 to-orange-400 ${item.width}`}
                ></div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Card */}
        <div className="mt-8 p-4 rounded-2xl bg-gradient-to-r from-pink-50 to-orange-50 border border-pink-100 flex items-center justify-between">

          <div>
            <p className="text-sm font-semibold text-gray-800">
              Trusted by Agencies
            </p>

            <p className="text-xs text-gray-500">
              Boost conversions instantly
            </p>
          </div>

          <ArrowRight className="w-5 h-5 text-purple-500" />
        </div>
      </div>
    </div>
  </div>
</section>
      <br />
      <br />

      {/* ✅ FEATURES SECTION */}
      <section id="features-overview" className="py-24 px-6 bg-white/70 backdrop-blur-xl rounded-t-[3rem] scroll-mt-28">
        <div className="max-w-7xl mx-auto text-center">

          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Powerful Features to Collect Testimonials
          </h2>

          <p className="text-gray-600 max-w-2xl mx-auto mb-14">
            Everything you need to gather, manage, and showcase authentic
            customer feedback.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: faVideo,
                title: "Video Testimonials",
                desc: "Collect authentic video feedback using simple recording tools.",
              },
              {
                icon: faBars,
                title: "Custom Widgets",
                desc: "Show testimonials using beautiful widgets tailored to your brand.",
              },
              {
                icon: faLink,
                title: "Easy Integration",
                desc: "Embed testimonials anywhere with simple copy-paste code.",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl shadow-xl p-8 hover:-translate-y-1 transition"
              >
                <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-gradient-to-br from-pink-500 to-orange-400 text-white mb-6 mx-auto">
                  <FontAwesomeIcon icon={item.icon} size="lg" />
                </div>
                <h3 className="text-xl font-semibold mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <br />
      <br />

      <div id="demo" className="min-h-screen bg-gradient-to-br from-pink-300 via-purple-200 to-orange-300 py-8 px-4 sm:px-6 lg:px-8 rounded-t-[6rem] scroll-mt-28">
        <div className="max-w-7xl mx-auto">
      <section className="bg-white/90 backdrop-blur-lg rounded-3xl shadow-2xl overflow-hidden mb-12 rounded-t-[3rem]">
            <div className="flex flex-col lg:flex-row">
              {/* Left Content Section */}
              <div className="flex-1 p-8 md:p-12 lg:p-16">
                
                {/* Tag */}
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500 to-orange-400 text-white px-4 py-2 rounded-full mb-6 shadow-lg">
                  <Sparkles className="w-4 h-4" />
                  <span className="text-sm">New Feature Available</span>
                </div>
                
                {/* Heading */}
                <h1 className="text-4xl md:text-5xl lg:text-6xl mb-6 leading-tight bg-gradient-to-r from-pink-600 to-orange-500 bg-clip-text text-transparent">
                  Build a Portfolio Page That Wins Client Trust
                </h1>
                
                {/* Subheading */}
                <p className="text-xl text-gray-700 mb-10 leading-relaxed max-w-lg">
                  Create a beautiful one-page portfolio to showcase your brand, testimonials, and contact details. 
                  Perfect for agencies, freelancers, and businesses.
                </p>
                
                {/* Feature List */}
                <div className="space-y-4 mb-10">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-orange-400 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <MessageSquare className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-lg text-gray-800">Collect testimonials effortlessly</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-orange-400 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <ZapIcon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-lg text-gray-800">Showcase your best work</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-orange-400 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <Heart className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-lg text-gray-800">Build trust with social proof</span>
                  </div>
                </div>
                
                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/signup"
                    className="group flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-pink-500 to-orange-400 text-white rounded-xl shadow-lg hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                  >
                    Create Portfolio Page
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <a
                    href="#examples"
                    className="flex items-center justify-center gap-2 px-8 py-4 bg-white text-pink-600 border-2 border-pink-300 rounded-xl hover:bg-pink-50 transition-all duration-300 shadow-md"
                  >
                    View Example
                  </a>
                </div>
                
                {/* Trust Badge */}
                <div className="flex items-center gap-2 mt-8 text-sm text-gray-600">
                  <Shield className="w-4 h-4 text-pink-500" />
                  <span>No credit card required • Setup in minutes</span>
                </div>
              </div>
              
              {/* Right Visual Section */}
              <div className="flex-1 p-8 lg:p-12 flex items-center justify-center bg-gradient-to-br from-pink-50 to-orange-50">
                <div className="relative w-full max-w-md">
                  
                  {/* Testimonial Cards Stack */}
                  <div className="space-y-4">
                    
                    {/* Card 1 */}
                    <div className="bg-white rounded-2xl p-6 shadow-xl transform rotate-2 hover:rotate-0 transition-transform duration-300 border border-pink-100">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-pink-400 to-orange-400 rounded-full flex items-center justify-center text-white">
                          ER
                        </div>
                        <div>
                          <p className="text-gray-900">Emily Rodriguez</p>
                          <p className="text-sm text-gray-500">CEO, TechStart</p>
                        </div>
                      </div>
                      <div className="flex gap-1 mb-3">
                        {[...Array(5)].map((_, i) => (
                          <svg key={i} className="w-5 h-5 text-orange-400 fill-current" viewBox="0 0 20 20">
                            <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                          </svg>
                        ))}
                      </div>
                      <p className="text-gray-600">"This platform helped us increase conversions by 40%. Best investment we made!"</p>
                    </div>
                    
                    {/* Card 2 */}
                    <div className="bg-white rounded-2xl p-6 shadow-xl transform -rotate-1 hover:rotate-0 transition-transform duration-300 border border-pink-100">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-pink-600 rounded-full flex items-center justify-center text-white">
                          MC
                        </div>
                        <div>
                          <p className="text-gray-900">Michael Chen</p>
                          <p className="text-sm text-gray-500">Marketing Director</p>
                        </div>
                      </div>
                      <div className="flex gap-1 mb-3">
                        {[...Array(5)].map((_, i) => (
                          <svg key={i} className="w-5 h-5 text-orange-400 fill-current" viewBox="0 0 20 20">
                            <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                          </svg>
                        ))}
                      </div>
                      <p className="text-gray-600">"Simple, powerful, and exactly what we needed. Highly recommend!"</p>
                    </div>
                    
                    {/* Card 3 (Partial) */}
                    <div className="bg-white/70 backdrop-blur rounded-2xl p-6 shadow-lg transform rotate-1 border border-pink-100">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-500 rounded-full flex items-center justify-center text-white">
                          SJ
                        </div>
                        <div>
                          <p className="text-gray-900">Sarah Johnson</p>
                          <p className="text-sm text-gray-500">Founder, GrowthCo</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        {[...Array(5)].map((_, i) => (
                          <svg key={i} className="w-5 h-5 text-orange-400 fill-current" viewBox="0 0 20 20">
                            <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                          </svg>
                        ))}
                      </div>
                    </div>
                    
                  </div>
                  
                  {/* Floating Badge */}
                  <div className="absolute -top-4 -right-4 bg-white rounded-full px-5 py-3 shadow-2xl border-4 border-orange-200">
                    <p className="text-2xl">⭐️ 4.9</p>
                  </div>
                  
                  {/* Floating Stats */}
                  <div className="absolute -bottom-4 -left-4 bg-gradient-to-br from-pink-500 to-orange-400 text-white rounded-2xl px-5 py-3 shadow-2xl">
                    <p className="text-xs opacity-90">Happy Clients</p>
                    <p className="text-2xl">10,000+</p>
                  </div>
                  
                </div>
              </div>
            </div>
          </section>
          </div>
          </div>

      {/* ✅ TESTIMONIALS */}
      {/* <section id="examples" className="py-24 px-6 bg-white/70 scroll-mt-28">
        <div className="max-w-7xl mx-auto text-center">

          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            What Our Customers Say
          </h2>

          <p className="text-gray-600 mb-14">
            Trusted by businesses worldwide
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "Emily K. Rodriguez",
                role: "Small Business Owner",
                text:
                  "A simple and effective solution. Collecting feedback is now a breeze!",
              },
              {
                name: "Michael Chen",
                role: "SaaS Founder",
                text:
                  "The video testimonials feature helped us build instant trust.",
              },
              {
                name: "Sarah Johnson",
                role: "Marketing Coach",
                text:
                  "Setup took minutes. We started getting responses immediately!",
              },
            ].map((t, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl shadow-lg p-8 text-left"
              >
                <p className="text-orange-400 mb-3 text-lg">★★★★★</p>
                <p className="text-gray-700 mb-6">"{t.text}"</p>
                <div>
                  <p className="font-semibold">{t.name}</p>
                  <p className="text-sm text-gray-500">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

    <section id="simple-process" className="py-20 px-6 bg-white overflow-hidden scroll-mt-28">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: -30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            transition={{ duration: 0.6 }}
          >
            <motion.span 
              className="inline-block bg-gradient-to-r from-pink-400 to-orange-400 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4"
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: false }}
              transition={{ duration: 0.5, delay: 0.2, type: "spring", stiffness: 200 }}
            >
              <a href="#simple-process" className="text-white">
                Simple Process
              </a>
            </motion.span>
            <motion.h2 
              className="text-3xl md:text-5xl font-extrabold mb-4 text-gray-900"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              How It Works
            </motion.h2>
            <motion.p 
              className="text-lg text-gray-600 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Start collecting and showcasing testimonials in minutes. Our simple 4-step process makes it effortless.
            </motion.p>
          </motion.div>

          {/* Steps Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
            
            {/* Connecting Line with Animation (Desktop) */}
            <motion.div 
              className="hidden lg:block absolute left-0 w-full h-0.5 bg-gradient-to-r from-pink-200 via-orange-200 to-pink-200" 
              style={{ top: '80px' }}
              initial={{ scaleX: 0, originX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: false, amount: 0.3 }}
              transition={{ duration: 1.5, delay: 0.5, ease: "easeInOut" }}
            ></motion.div>

            {/* Step 1 */}
            <motion.div 
              className="relative"
              initial={{ opacity: 0, y: 50, rotateY: -15 }}
              whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
              viewport={{ once: false, amount: 0.3 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <motion.div 
                className="bg-gradient-to-br from-pink-50 to-orange-50 rounded-2xl p-8 text-center"
                whileHover={{ 
                  y: -10, 
                  boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                  transition: { duration: 0.3 }
                }}
              >
                <motion.div 
                  className="w-20 h-20 mx-auto bg-gradient-to-br from-pink-400 to-orange-400 rounded-2xl flex items-center justify-center mb-6 relative z-10"
                  initial={{ scale: 0, rotate: -180 }}
                  whileInView={{ scale: 1, rotate: 0 }}
                  viewport={{ once: false }}
                  transition={{ duration: 0.6, delay: 0.8, type: "spring", stiffness: 200 }}
                  whileHover={{ 
                    rotate: [0, -10, 10, -10, 0],
                    transition: { duration: 0.5 }
                  }}
                >
                  <FileText className="w-10 h-10 text-white" strokeWidth={2} />
                </motion.div>
                <motion.div 
                  className="inline-block bg-gradient-to-r from-pink-400 to-orange-400 text-white px-3 py-1 rounded-full text-sm font-semibold mb-3"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: false }}
                  transition={{ duration: 0.5, delay: 1 }}
                >
                  Step 1
                </motion.div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">
                  Create Your Form
                </h3>
                <p className="text-gray-600">
                  Set up a custom testimonial collection form in seconds. Customize questions to fit your needs.
                </p>
              </motion.div>
            </motion.div>

            {/* Step 2 */}
            <motion.div 
              className="relative"
              initial={{ opacity: 0, y: 50, rotateY: -15 }}
              whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
              viewport={{ once: false, amount: 0.3 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <motion.div 
                className="bg-gradient-to-br from-pink-50 to-orange-50 rounded-2xl p-8 text-center"
                whileHover={{ 
                  y: -10, 
                  boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                  transition: { duration: 0.3 }
                }}
              >
                <motion.div 
                  className="w-20 h-20 mx-auto bg-gradient-to-br from-pink-400 to-orange-400 rounded-2xl flex items-center justify-center mb-6 relative z-10"
                  initial={{ scale: 0, rotate: -180 }}
                  whileInView={{ scale: 1, rotate: 0 }}
                  viewport={{ once: false }}
                  transition={{ duration: 0.6, delay: 1, type: "spring", stiffness: 200 }}
                  whileHover={{ 
                    rotate: [0, -10, 10, -10, 0],
                    transition: { duration: 0.5 }
                  }}
                >
                  <Share2 className="w-10 h-10 text-white" strokeWidth={2} />
                </motion.div>
                <motion.div 
                  className="inline-block bg-gradient-to-r from-pink-400 to-orange-400 text-white px-3 py-1 rounded-full text-sm font-semibold mb-3"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: false }}
                  transition={{ duration: 0.5, delay: 1.2 }}
                >
                  Step 2
                </motion.div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">
                  Collect Testimonials
                </h3>
                <p className="text-gray-600">
                  Share your unique link with customers via email, social media, or your website to gather feedback.
                </p>
              </motion.div>
            </motion.div>

            {/* Step 3 */}
            <motion.div 
              className="relative"
              initial={{ opacity: 0, y: 50, rotateY: -15 }}
              whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
              viewport={{ once: false, amount: 0.3 }}
              transition={{ duration: 0.6, delay: 1 }}
            >
              <motion.div 
                className="bg-gradient-to-br from-pink-50 to-orange-50 rounded-2xl p-8 text-center"
                whileHover={{ 
                  y: -10, 
                  boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                  transition: { duration: 0.3 }
                }}
              >
                <motion.div 
                  className="w-20 h-20 mx-auto bg-gradient-to-br from-pink-400 to-orange-400 rounded-2xl flex items-center justify-center mb-6 relative z-10"
                  initial={{ scale: 0, rotate: -180 }}
                  whileInView={{ scale: 1, rotate: 0 }}
                  viewport={{ once: false }}
                  transition={{ duration: 0.6, delay: 1.2, type: "spring", stiffness: 200 }}
                  whileHover={{ 
                    rotate: [0, -10, 10, -10, 0],
                    transition: { duration: 0.5 }
                  }}
                >
                  <CheckCircle className="w-10 h-10 text-white" strokeWidth={2} />
                </motion.div>
                <motion.div 
                  className="inline-block bg-gradient-to-r from-pink-400 to-orange-400 text-white px-3 py-1 rounded-full text-sm font-semibold mb-3"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: false }}
                  transition={{ duration: 0.5, delay: 1.4 }}
                >
                  Step 3
                </motion.div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">
                  Manage & Curate
                </h3>
                <p className="text-gray-600">
                  Review, approve, and organize testimonials in your dashboard. Keep only the best ones.
                </p>
              </motion.div>
            </motion.div>

            {/* Step 4 */}
            <motion.div 
              className="relative"
              initial={{ opacity: 0, y: 50, rotateY: -15 }}
              whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
              viewport={{ once: false, amount: 0.3 }}
              transition={{ duration: 0.6, delay: 1.2 }}
            >
              <motion.div 
                className="bg-gradient-to-br from-pink-50 to-orange-50 rounded-2xl p-8 text-center"
                whileHover={{ 
                  y: -10, 
                  boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                  transition: { duration: 0.3 }
                }}
              >
                <motion.div 
                  className="w-20 h-20 mx-auto bg-gradient-to-br from-pink-400 to-orange-400 rounded-2xl flex items-center justify-center mb-6 relative z-10"
                  initial={{ scale: 0, rotate: -180 }}
                  whileInView={{ scale: 1, rotate: 0 }}
                  viewport={{ once: false }}
                  transition={{ duration: 0.6, delay: 1.4, type: "spring", stiffness: 200 }}
                  whileHover={{ 
                    rotate: [0, -10, 10, -10, 0],
                    transition: { duration: 0.5 }
                  }}
                >
                  <Sparkles className="w-10 h-10 text-white" strokeWidth={2} />
                </motion.div>
                <motion.div 
                  className="inline-block bg-gradient-to-r from-pink-400 to-orange-400 text-white px-3 py-1 rounded-full text-sm font-semibold mb-3"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: false }}
                  transition={{ duration: 0.5, delay: 1.6 }}
                >
                  Step 4
                </motion.div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">
                  Display Anywhere
                </h3>
                <p className="text-gray-600">
                  Embed beautiful testimonial widgets on your website with a simple copy-paste code snippet.
                </p>
              </motion.div>
            </motion.div>

          </div>

          {/* Bottom CTA */}
          <motion.div 
            className="text-center mt-12"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: false }}
            transition={{ duration: 0.6, delay: 1.8 }}
          >
            <motion.div
              whileHover={{ 
                scale: 1.05,
                boxShadow: "0 20px 40px rgba(251, 146, 60, 0.4)",
                transition: { duration: 0.3 }
              }}
              whileTap={{ scale: 0.95 }}
              className="inline-block"
            >
              <Link
                to="/signup"
                className="inline-block px-8 py-4 bg-gradient-to-r from-pink-400 to-orange-400 text-white font-semibold rounded-xl shadow-lg"
              >
                Get Started Now - It's Free
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>



      <section id="examples" className="py-24 px-6 bg-white/70 scroll-mt-28">
        <div className="max-w-7xl mx-auto text-center">

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-pink-500 to-orange-400 bg-clip-text text-transparent">
              What Our Customers Say
            </h2>

            <p className="text-gray-600 mb-14">
              Trusted by businesses worldwide
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                name: "Emily K. Rodriguez",
                role: "Small Business Owner",
                avatar: "https://images.unsplash.com/photo-1649589244330-09ca58e4fa64?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB3b21hbiUyMHBvcnRyYWl0fGVufDF8fHx8MTc2NDI4Nzg0Mnww&ixlib=rb-4.1.0&q=80&w=1080",
                text: "A simple and effective solution. Collecting feedback is now a breeze!",
                rating: 5,
              },
              {
                name: "Michael Chen",
                role: "SaaS Founder",
                avatar: "https://images.unsplash.com/photo-1598268012815-ae21095df31b?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBtYW4lMjBidXNpbmVzc3xlbnwxfHx8fDE3NjQyNDcwNTB8MA&ixlib=rb-4.1.0&q=80&w=1080",
                text: "The video testimonials feature helped us build instant trust.",
                rating: 5,
              },
              {
                name: "Sarah Johnson",
                role: "Marketing Coach",
                avatar: "https://images.unsplash.com/photo-1655249493799-9cee4fe983bb?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBwZXJzb24lMjBoZWFkc2hvdHxlbnwxfHx8fDE3NjQzMjg2MDh8MA&ixlib=rb-4.1.0&q=80&w=1080",
                text: "Setup took minutes. We started getting responses immediately!",
                rating: 5,
              },
              {
                name: "David Martinez",
                role: "E-commerce Owner",
                avatar: "https://images.unsplash.com/photo-1629507208649-70919ca33793?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbCUyMHBvcnRyYWl0fGVufDF8fHx8MTc2NDI4OTgzMXww&ixlib=rb-4.1.0&q=80&w=1080",
                text: "Our conversion rate increased by 40% after adding testimonials to our product pages!",
                rating: 5,
              },
              {
                name: "Jessica Thompson",
                role: "Digital Agency CEO",
                avatar: "https://images.unsplash.com/photo-1617386124435-9eb3935b1e11?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxlbnRyZXByZW5ldXIlMjBwb3J0cmFpdHxlbnwxfHx8fDE3NjQyNTg3NDl8MA&ixlib=rb-4.1.0&q=80&w=1080",
                text: "The customization options are fantastic. It perfectly matches our brand identity.",
                rating: 5,
              },
              {
                name: "Robert Kim",
                role: "Tech Startup Founder",
                avatar: "https://images.unsplash.com/photo-1655249493799-9cee4fe983bb?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBwZXJzb24lMjBoZWFkc2hvdHxlbnwxfHx8fDE3NjQzMjg2MDh8MA&ixlib=rb-4.1.0&q=80&w=1080",
                text: "Best testimonial tool I've used. The analytics helped us understand what resonates with customers.",
                rating: 5,
              },
              {
                name: "Amanda Foster",
                role: "Course Creator",
                avatar: "https://images.unsplash.com/photo-1649589244330-09ca58e4fa64?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB3b21hbiUyMHBvcnRyYWl0fGVufDF8fHx8MTc2NDI4Nzg0Mnww&ixlib=rb-4.1.0&q=80&w=1080",
                text: "My students love leaving video testimonials. It's made my sales page so much more authentic!",
                rating: 5,
              },
              {
                name: "James Patterson",
                role: "Consultant",
                avatar: "https://images.unsplash.com/photo-1598268012815-ae21095df31b?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBtYW4lMjBidXNpbmVzc3xlbnwxfHx8fDE3NjQyNDcwNTB8MA&ixlib=rb-4.1.0&q=80&w=1080",
                text: "The automatic follow-up emails get us 3x more testimonials than we used to collect.",
                rating: 5,
              },
              {
                name: "Lisa Anderson",
                role: "Fitness Coach",
                avatar: "https://images.unsplash.com/photo-1629507208649-70919ca33793?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbCUyMHBvcnRyYWl0fGVufDF8fHx8MTc2NDI4OTgzMXww&ixlib=rb-4.1.0&q=80&w=1080",
                text: "I love how easy it is to showcase before-and-after transformations with testimonials!",
                rating: 5,
              },
            ].map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{ y: -8, transition: { duration: 0.2 } }}
                className="bg-white rounded-2xl shadow-lg p-8 text-left hover:shadow-2xl transition-shadow duration-300 border border-gray-100 relative overflow-hidden group"
              >
                {/* Gradient accent on hover */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-pink-500 to-orange-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                
                {/* Quote icon */}
                <div className="absolute top-6 right-6 opacity-10">
                  <Quote className="w-12 h-12 text-pink-500" />
                </div>

                {/* Stars */}
                <div className="flex gap-1 mb-4">
                  {[...Array(t.rating)].map((_, index) => (
                    <Star
                      key={index}
                      className="w-5 h-5 text-orange-400 fill-orange-400"
                    />
                  ))}
                </div>

                {/* Testimonial text */}
                <p className="text-gray-700 mb-6 relative z-10 italic">
                  "{t.text}"
                </p>

                {/* Author info */}
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-gradient-to-r from-pink-500 to-orange-400">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{t.name}</p>
                    <p className="text-sm text-gray-500">{t.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA at bottom */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-16"
          >
            <Link
              to="/signup"
              className="inline-block px-8 py-4 bg-gradient-to-r from-pink-500 to-orange-400 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              Start Collecting Testimonials
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ✅ FAQ */}
      <section id="faq" className="py-24 px-6 bg-white/70 scroll-mt-28">
        <div className="max-w-4xl mx-auto text-center">

          <h2 className="text-3xl font-bold mb-4">
            Frequently Asked Questions
          </h2>

          <p className="text-gray-600 mb-12">
            Everything you need to know
          </p>

          <div className="space-y-4 text-left">
            {homepageFaqs.map((faq) => (
              <div
                key={faq.question}
                className="bg-white rounded-2xl p-6 shadow-md border border-gray-100 hover:bg-gray-50 transition"
              >
                <h3 className="text-lg font-bold text-gray-900">{faq.question}</h3>
                <p className="mt-3 text-gray-600 leading-7">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      
      
      <section className="relative py-28 px-6 text-center text-white overflow-hidden
  bg-gradient-to-r from-pink-500 via-purple-500 to-orange-500">

  {/* Dynamic glow overlay */}
  <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-black/10" />

  {/* Radial highlight */}
  <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.25),transparent_70%)]" />

  <div className="relative max-w-4xl mx-auto">
    <h2 className="text-4xl md:text-5xl font-extrabold mb-6 drop-shadow-lg">
      Ready to Boost Conversions?
    </h2>

    <p className="text-lg text-white/90 mb-10">
      Turn real customer testimonials into trust, credibility, and more sales.
    </p>

    <div className="flex flex-col sm:flex-row justify-center gap-5">
      <Link
        to="/signup"
        className="px-10 py-4 bg-white text-purple-700 font-bold 
        rounded-xl shadow-2xl hover:scale-105 transition"
      >
        Start Free Trial
      </Link>

      <Link
        to="/pricing"
        className="px-10 py-4 border-2 border-white/70 
        text-white font-semibold rounded-xl hover:bg-white/20 transition"
      >
        View Pricing
      </Link>
    </div>

    <p className="mt-6 text-sm text-white/80">
      No credit card required • Setup in minutes
    </p>
  </div>
</section>

      {/* ✅ CTA
      <section className="py-24 px-6 bg-gradient-to-br from-purple-400 via-pink-400 to-orange-400 text-center text-white">
        <div className="max-w-4xl mx-auto">

          <h2 className="text-4xl font-extrabold mb-6">
            Ready to Boost Conversions?
          </h2>

          <p className="text-lg mb-10">
            Join thousands of businesses using social proof to grow faster.
          </p>

          <Link
            to="/signup"
            className="inline-block px-10 py-4 bg-white text-purple-700 font-semibold rounded-xl shadow-lg hover:bg-gray-100"
          >
            Start Your Free Trial
          </Link>
        </div>
      </section> */}

      {/* Footer */}
      <Footer />
    </div>
    </>
  );
};

export default Homepage;
